import React, { useState } from 'react';
import styled from 'styled-components';

import { Button, Input, Modal, Typography, Form, Select, Spin, notification, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router';

import { useSearchAcrossEntitiesWithSearchAfterQuery } from '../../../../../../graphql/search.generated';
import { AndFilterInput, EntityType, SearchAcrossEntitiesInput } from '../../../../../../types.generated';

import { getSearchCsvDownloadHeader, transformResultsToCsvRow } from './downloadAsCsvUtil';
import { downloadRowsAsCsv } from '../../../../../search/utils/csvUtils';
import { useEntityRegistry } from '../../../../../useEntityRegistry';
import { useEntityData } from '../../../EntityContext';
import analytics, { EventType } from '../../../../../analytics';
import { SearchResultsInterface } from './types';
import { useTranslation } from '../../../../../../i18n/useTranslation';

type Props = {
    callSearchOnVariables?: (variables: {
        input: SearchAcrossEntitiesInput;
    }) => Promise<SearchResultsInterface | null | undefined>;
    downloadType: any;
    filters: AndFilterInput[];
    query: string;
    entityFilters: EntityType[];
    totalResults?: number;
    setIsDownloadingCsv: (isDownloadingCsv: boolean) => any;
    showDownloadAsCsvModal: boolean;
    setShowDownloadAsCsvModal: (showDownloadAsCsvModal: boolean) => any;
    isDownloadingCsv: boolean;
};

const StyledItem = styled(Form.Item)`
    margin-bottom: 0;
`;

const SEARCH_PAGE_SIZE_FOR_DOWNLOAD = 100;

export default function DownloadAsCsvModal({
    callSearchOnVariables,
    filters,
    query,
    entityFilters,
    totalResults,
    downloadType,
    setIsDownloadingCsv,
    showDownloadAsCsvModal,
    setShowDownloadAsCsvModal,
    isDownloadingCsv,
}: Props) {
    const { t } = useTranslation();
    const { entityData: entitySearchIsEmbeddedWithin } = useEntityData();
    const { refetch: downloadSearchResults } = useSearchAcrossEntitiesWithSearchAfterQuery({
        skip: true,
    });
    const location = useLocation();
    const [form] = Form.useForm();

    const [buttonDisabled, setButtonDisabled] = useState(false);
    const initialValues = {
        name: entitySearchIsEmbeddedWithin ? `${entitySearchIsEmbeddedWithin.name}_impact` : 'results',
        'file-format': 'csv',
    };

    const entityRegistry = useEntityRegistry();

    const openNotification = () => {
        notification.info({
            message: t('Preparing Download'),
            description: totalResults
                ? t('Creating CSV with {{totalResults}} entities to download', { totalResults })
                : t('Creating CSV to download'),
            placement: 'bottomRight',
            duration: null,
            icon: <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />,
        });
    };

    const closeNotification = () => {
        setTimeout(() => {
            notification.destroy();
        }, 3000);
    };

    const showFailedDownloadNotification = () => {
        notification.destroy();
        notification.error({
            message: t('Download Failed'),
            description: t('The CSV file could not be downloaded'),
            placement: 'bottomRight',
            duration: 3,
        });
    };

    const triggerCsvDownload = (filename: string) => {
        setIsDownloadingCsv(true);
        openNotification();

        let downloadPage = 1;
        let accumulatedResults: string[][] = [];

        analytics.event({
            type: EventType.DownloadAsCsvEvent,
            query,
            entityUrn: entitySearchIsEmbeddedWithin?.urn,
            path: location.pathname,
        });

        const handleError = () => {
            setIsDownloadingCsv(false);
            setShowDownloadAsCsvModal(false);
            showFailedDownloadNotification();
        };

        const closeModal = () => {
            setIsDownloadingCsv(false);
            setShowDownloadAsCsvModal(false);
            closeNotification();
        };

        // Forward declarations
        let fetchNextPage: (searchAfterParam?: any) => void;

        // Function implementations
        const processResults = (results: any[], searchAfter?: any) => {
            if (!results?.length) {
                closeModal();
                downloadRowsAsCsv(getSearchCsvDownloadHeader(), accumulatedResults, filename);
                return;
            }

            accumulatedResults = [...accumulatedResults, ...transformResultsToCsvRow(results, entityRegistry)];

            if (searchAfter) {
                fetchNextPage(searchAfter);
            } else if (totalResults && downloadPage * SEARCH_PAGE_SIZE_FOR_DOWNLOAD < totalResults) {
                downloadPage += 1;
                fetchNextPage();
            } else {
                closeModal();
                downloadRowsAsCsv(getSearchCsvDownloadHeader(results[0]), accumulatedResults, filename);
            }
        };

        fetchNextPage = (searchAfterParam: any = null) => {
            const input = {
                types: entityFilters,
                query,
                start: SEARCH_PAGE_SIZE_FOR_DOWNLOAD * (downloadPage - 1),
                count: SEARCH_PAGE_SIZE_FOR_DOWNLOAD,
                orFilters: filters,
            };
            if (callSearchOnVariables) {
                callSearchOnVariables({
                    input,
                })
                    .then((refetchData) => {
                        processResults(refetchData?.searchResults || []);
                    })
                    .catch(handleError);
            } else {
                downloadSearchResults({
                    input: {
                        searchInput: input,
                        searchAfter: searchAfterParam,
                    },
                })
                    .then((res) => {
                        const { searchAcrossEntitiesWithSearchAfter: searchAcrossEntities } = res?.data || {};

                        const searchAfter = searchAcrossEntities?.searchAfter
                            ? { entityId: searchAcrossEntities?.searchAfter?.entityId }
                            : null;

                        const results = searchAcrossEntities?.results.searchResults || [];

                        processResults(results, searchAfter);
                    })
                    .catch(handleError);
            }
        };

        fetchNextPage();
    };

    const onFinish = (values: any) => {
        const { checkDownloadType, exportDataProductAssets }: any = downloadType || {};

        if (checkDownloadType || exportDataProductAssets) {
            if (checkDownloadType?.type || exportDataProductAssets?.type) {
                triggerCsvDownload(`${values.name}.${values['file-format']}`);
            } else {
                message.error(t('Something went wrong'));
            }
        }
    };
    return (
        <Modal
            centered
            className="f-modal"
            onCancel={() => {
                if (!isDownloadingCsv) {
                    setShowDownloadAsCsvModal(false);
                }
            }}
            title={t('Download as...')}
            visible={showDownloadAsCsvModal}
            footer={
                <>
                    <Button onClick={() => setShowDownloadAsCsvModal(false)} disabled={isDownloadingCsv} type="default">
                        {t('Close')}
                    </Button>
                    <Button
                        key="submit"
                        type="primary"
                        htmlType="submit"
                        form="download-as"
                        data-testid="csv-modal-download-button"
                        disabled={buttonDisabled}
                        loading={isDownloadingCsv}
                    >
                        {t('Download')}
                    </Button>
                </>
            }
        >
            <Form
                onFinish={onFinish}
                form={form}
                layout="vertical"
                id="download-as"
                placeholder="datahub.csv"
                initialValues={initialValues}
                onFieldsChange={() => {
                    setButtonDisabled(form.getFieldsError().filter(({ errors }) => errors.length).length > 0);
                }}
            >
                <Form.Item label={<Typography.Text strong>{t('Name')}</Typography.Text>}>
                    <StyledItem
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: 'required',
                            },
                        ]}
                    >
                        <Input placeholder="datahub" />
                    </StyledItem>
                </Form.Item>
                <Form.Item label={<Typography.Text strong>{t('Format Type')}</Typography.Text>}>
                    <StyledItem name="file-format">
                        <Select
                            aria-label="Select file format"
                            getPopupContainer={(triggerNode) => triggerNode.parentNode} // Control dropdown container
                        >
                            <Select.Option value="csv" tabIndex={0}>
                                {t('csv')}
                            </Select.Option>
                        </Select>
                    </StyledItem>
                </Form.Item>
            </Form>
        </Modal>
    );
}
